import {
  AppDialog,
  FormField,
  PillTabs,
  SelectField,
} from "@/components/common";
import { OrgTreeSelect } from "@/components/common/OrgTreeSelect";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useTran } from "@/hooks";
import { Form, Formik } from "formik";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import {
  InternalAssignmentSchema,
  ExternalAssignmentSchema,
} from "../../schemas/assignment.schema";
import {
  useAssignmentStore,
  useDisclosureStore,
  useEntryDataStore,
} from "../../hooks/useDataEntryStore";
import {
  createAssignment,
  loadCompanyUsers,
} from "../../operations/assignment.ops";

const AssignDisclosureDialog = observer(() => {
  const { t } = useTran();
  const formikRef = useRef();
  const { showAssignDialog, setShowAssignDialog, companyUsers } =
    useAssignmentStore();
  const { activeDisclosure } = useDisclosureStore();
  const framework = activeDisclosure?.framework || "";
  const { filteredOrgTree } = useEntryDataStore();

  const [mode, setMode] = useState("internal");
  const [formikDirty, setFormikDirty] = useState(false);

  useEffect(() => {
    if (showAssignDialog) {
      loadCompanyUsers();
      setMode("internal");
    }
  }, [showAssignDialog]);

  const userOptions = companyUsers.map((u) => ({
    key: u.email,
    label: `${u.firstName || ""} ${u.lastName || ""} (${u.email})`.trim(),
  }));

  const handleModeChange = (newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    formikRef.current?.resetForm();
  };

  const handleCancel = () => {
    setShowAssignDialog(false);
  };

  const handleSubmit = async (values) => {
    await createAssignment({
      disclosureId: activeDisclosure._id,
      framework,
      email: values.email.trim(),
      ...(mode === "external" && { organizationId: values.organizationId }),
    });
    setShowAssignDialog(false);
  };

  const validationSchema =
    mode === "internal" ? InternalAssignmentSchema : ExternalAssignmentSchema;

  return (
    <AppDialog
      size="md"
      open={showAssignDialog}
      onOpenChange={(openState) => {
        if (!openState) setShowAssignDialog(false);
        else setShowAssignDialog(true);
      }}
      isDirty={formikDirty}
      title={t("Assign Disclosure")}
      description={t(
        "Assign this disclosure to a user for data entry. They will receive an email notification.",
      )}
      footer={
        <div className="flex gap-x-2 items-center justify-end">
          <Button variant="outline" onClick={handleCancel}>
            {t("Cancel")}
          </Button>
          <Button onClick={() => formikRef.current.submitForm()}>
            {t("Assign")}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {activeDisclosure && (
          <div className="p-3 bg-muted rounded-md">
            <p className="text-xs text-muted-foreground">{t("Disclosure")}</p>
            <p className="text-sm font-medium">
              {activeDisclosure.code} - {t(activeDisclosure.title)}
            </p>
          </div>
        )}

        <PillTabs
          tabs={[
            { value: "internal", label: t("Organization User") },
            { value: "external", label: t("External Invite") },
          ]}
          value={mode}
          onChange={handleModeChange}
        />

        <Formik
          initialValues={{ email: "", organizationId: "" }}
          innerRef={formikRef}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({
            errors,
            touched,
            values,
            handleChange,
            setFieldValue,
            dirty,
          }) => {
            if (dirty !== formikDirty) setFormikDirty(dirty);
            return (
              <Form className="space-y-3">
                {mode === "internal" ? (
                  <SelectField
                    label={t("User")}
                    options={userOptions}
                    value={values.email}
                    onValueChange={(val) => setFieldValue("email", val)}
                    placeholder={t("Search and select a user")}
                    error={touched.email && errors.email}
                    // searchable
                    searchPlaceholder={t("Search by name or email")}
                    emptyText={t("No users found")}
                    required
                  />
                ) : (
                  <>
                    <div className="space-y-2 w-full">
                      <Label>
                        {t("For which organization")}
                        <span className="ml-1 text-destructive">*</span>
                      </Label>
                      <OrgTreeSelect
                        tree={filteredOrgTree}
                        value={values.organizationId}
                        onChange={(val) => setFieldValue("organizationId", val)}
                        placeholder={t("Select organization")}
                        showAllOption={false}
                        className="w-full"
                      />
                      {touched.organizationId && errors.organizationId && (
                        <p className="text-xs text-destructive">
                          {errors.organizationId}
                        </p>
                      )}
                    </div>
                    <FormField
                      name="email"
                      label={t("Email")}
                      placeholder={t("Enter assignee email")}
                      value={values.email}
                      onChange={handleChange}
                      error={touched.email && errors.email}
                      required
                    />
                  </>
                )}
              </Form>
            );
          }}
        </Formik>
      </div>
    </AppDialog>
  );
});

export default AssignDisclosureDialog;
